import {
  PASSWORD_MIN_LENGTH,
  passwordResetRequestSchema,
  singleUseTokenSchema,
} from '@mfos/shared';
import { useMutation } from '@tanstack/react-query';
import { type SubmitEvent, useState } from 'react';
import { Link } from 'react-router';
import { ErrorNotice, SuccessNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { PublicLayout } from '../../components/PublicLayout';
import { validate, type FieldErrors } from '../../lib/validation';
import { resetPassword } from './auth.api';
import { PASSWORD_UPDATED_STATE } from './login-notice';
import { useFragmentToken } from './use-fragment-token';
import { phrase } from '../../lib/i18n/runtime';

export function ResetPasswordPage() {
  const token = useFragmentToken();
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const mutation = useMutation({ mutationFn: resetPassword });

  if (token === null || !singleUseTokenSchema.safeParse(token).success) {
    return (
      <PublicLayout title={phrase('Choose a new password')}>
        <p>
          This reset link is incomplete or invalid. Open the link from your email again, or{' '}
          <Link to="/forgot-password">{phrase('request a new one')}</Link>.
        </p>
      </PublicLayout>
    );
  }

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const result = validate(passwordResetRequestSchema, { token, newPassword });
    const nextErrors: Record<string, string> = result.ok ? {} : { ...result.errors };
    if (confirmation !== newPassword) {
      nextErrors.confirmation = 'The passwords do not match.';
    }
    setErrors(nextErrors);
    if (result.ok && Object.keys(nextErrors).length === 0) {
      mutation.mutate(result.data);
    }
  }

  return (
    <PublicLayout title={phrase('Choose a new password')}>
      {mutation.isSuccess ? (
        <>
          <SuccessNotice>
            {phrase('Your password was updated. Sign in with your new password.')}
          </SuccessNotice>
          <Link to="/login" state={PASSWORD_UPDATED_STATE} className="button">
            {phrase('Sign in')}
          </Link>
        </>
      ) : (
        <form onSubmit={submit} noValidate className="stack">
          <TextField
            label={phrase('New password')}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onValueChange={setNewPassword}
            error={errors.newPassword ?? errors.token}
            hint={`At least ${String(PASSWORD_MIN_LENGTH)} characters. A long passphrase works well.`}
            required
          />
          <TextField
            label={phrase('Confirm new password')}
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onValueChange={setConfirmation}
            error={errors.confirmation}
            required
          />
          <ErrorNotice error={mutation.error} />
          <button type="submit" className="button" disabled={mutation.isPending}>
            {mutation.isPending ? phrase('Saving…') : phrase('Set new password')}
          </button>
        </form>
      )}
    </PublicLayout>
  );
}
